import { useState } from "react";
import { BadgeCheck, Mail, Search, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { useAuth } from "../auth";

const STATUS_CLS = {
    valid: "border-emerald-500/40 text-emerald-400",
    used: "border-lakou-gold/40 text-lakou-gold",
    cancelled: "border-red-500/40 text-red-400",
    refunded: "border-red-500/40 text-red-400",
};

export const ControlGuests = () => {
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [guests, setGuests] = useState([]);
    const [searched, setSearched] = useState(false);
    const [busy, setBusy] = useState(false);
    const canManage = ["admin", "manager"].includes(user?.role);

    const search = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.get("/control/guests", { params: { q } });
            setGuests(data.guests || []);
            setSearched(true);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const resend = async (orderId) => {
        try {
            const { data } = await api.post("/control/guests/resend", { order_id: orderId });
            toast.success(`Ticket re-sent to ${data.email}`);
        } catch (err) {
            toast.error(apiError(err, "Could not resend"));
        }
    };

    return (
        <div className="space-y-5" data-testid="control-guests">
            <h1 className="font-display font-black text-xl">Guest Management</h1>
            <form onSubmit={search} className="flex gap-2">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-lakou-sand" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="guest-search"
                        placeholder="Name, email, phone or ticket number"
                        className="w-full rounded-full border border-lakou-gold/20 bg-lakou-coal/70 pl-11 pr-4 py-3.5 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors" />
                </div>
                <button type="submit" disabled={busy} data-testid="guest-search-btn"
                    className="rounded-full bg-lakou-gold text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-6 transition-opacity disabled:opacity-50">
                    {busy ? "…" : "Search"}
                </button>
            </form>

            <div className="space-y-4" data-testid="guest-results">
                {guests.map(({ order: o, tickets }) => (
                    <div key={o.id} className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-4 space-y-3" data-testid={`guest-${o.id.slice(0, 8)}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-display font-bold text-lakou-cream truncate">{o.name}</div>
                                <div className="text-xs text-lakou-grey truncate">{o.email}{o.phone ? ` · ${o.phone}` : ""}</div>
                                <div className="mt-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-lakou-sand">
                                    {(o.payment_method || "").toUpperCase()} · ${o.amount?.toFixed(2)} · {o.reference || o.id.slice(0, 8)}
                                </div>
                            </div>
                            <span className={`shrink-0 rounded-full border px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] ${o.status === "paid" ? "border-emerald-500/40 text-emerald-400" : "border-lakou-gold/40 text-lakou-gold"}`} data-testid="guest-status">
                                {o.status}
                            </span>
                        </div>
                        <div className="space-y-1.5">
                            {tickets.map((t) => (
                                <div key={t.code} className="flex items-center justify-between rounded-xl bg-lakou-raise/60 px-3.5 py-2.5">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <Ticket size={13} className="text-lakou-gold shrink-0" />
                                        <span className="font-mono2 text-[11px] text-lakou-cream tracking-wider">{t.code}</span>
                                        <span className="font-mono2 text-[9px] text-lakou-sand truncate">{t.category}</span>
                                    </div>
                                    <span className={`rounded-full border px-2.5 py-0.5 font-mono2 text-[8px] uppercase tracking-[0.15em] ${STATUS_CLS[t.status] || "border-lakou-gold/30 text-lakou-sand"}`}>
                                        {t.status}{t.status === "used" && t.checked_in_by ? ` · ${t.checked_in_by}` : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {canManage && o.status === "paid" && tickets.length > 0 && (
                            <button onClick={() => resend(o.id)} data-testid={`resend-${o.id.slice(0, 8)}`}
                                className="inline-flex items-center gap-2 rounded-full border border-lakou-gold/30 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-lakou-gold hover:bg-lakou-gold/10 transition-colors">
                                <Mail size={12} /> Resend ticket email
                            </button>
                        )}
                    </div>
                ))}
                {searched && !guests.length && <p className="text-sm text-lakou-sand" data-testid="guest-empty">No guests found.</p>}
            </div>
        </div>
    );
};
